/**
 * A state's spritesheet playback as the operations take it (spec 035): the
 * stored `StatePlayback`, whose `whenDone` is one of the strings `'hold-last'`
 * and `'hold-first'` (spec 033 D-7). `getSprite` reports it in this form, so a
 * value read can be passed straight back.
 */
import type { StatePlayback } from '../@types';
import { PLAYBACK_SPEED_MAX, PLAYBACK_SPEED_MIN } from '../utils/spriteSheet';
import type { JsonSchema } from './types';

/** Since spec 033 D-7 the operations' form is the stored one. */
export type PlaybackArg = StatePlayback;

export const PLAYBACK_ARG_SCHEMA: JsonSchema = {
  type: 'object',
  description: 'How this state plays its picture when the picture is a spritesheet (listMedia says "sheet: n frames").',
  properties: {
    mode: { type: 'string', enum: ['loop', 'once', 'pingpong', 'still'], description: 'loop repeats, once plays one cycle, pingpong plays forwards then back (both until the next state key), still shows the first frame.' },
    speed: { type: 'number', description: `Multiplier on the sheet's own timing (${PLAYBACK_SPEED_MIN}–${PLAYBACK_SPEED_MAX}); omit for 1.` },
    range: { type: 'array', description: 'The first and last frame played, [first, last], 0-based; omit for every frame.', items: { type: 'integer', description: 'A frame index, 0-based.' } },
    whenDone: { type: 'string', enum: ['hold-last', 'hold-first'], description: "once only: the frame held after its cycle, 'hold-last' (the default) or 'hold-first'. To change state afterwards, add a state key." },
    randomStart: { type: 'boolean', description: 'Start each sprite on its own frame, so a crowd does not move in step.' },
  },
  required: ['mode'],
  additionalProperties: false,
};

/**
 * The stored playback of an operation's argument. Since spec 033 D-7 a
 * playback names no other state, so this only copies: a `whenDone` other than
 * the two holds (or a `loops`), which the schema does not offer but a model
 * may still send, is passed on for `validateStatePlayback` to refuse by name,
 * which every caller runs before storing.
 */
export function playbackFromArg(arg: PlaybackArg): StatePlayback {
  return { ...arg } as StatePlayback;
}

/** A stored playback as the operations take it (and `getSprite` reports it). */
export function playbackToArg(playback: StatePlayback): PlaybackArg {
  return { ...playback };
}
